import { api, ApiError } from './client';

export type EsignStatus = 'draft' | 'sent' | 'completed' | 'cancelled';
export type EsignSignerStatus = 'waiting' | 'sent' | 'viewed' | 'signed';
export type EsignFieldType = 'signature' | 'text';
export type EsignSignatureMode = 'typed' | 'drawn';
export type EsignFileKind = 'original' | 'signed' | 'certificate';

export interface EsignSigner {
  id: number;
  order: number;
  firstName: string;
  lastName: string;
  email: string;
  partnerId: number | null;
  leadId: number | null;
  companyName: string | null;
  status: EsignSignerStatus;
  sentAt: string | null;
  remindedAt: string | null;
  viewedAt: string | null;
  signedAt: string | null;
}

export interface EsignField {
  id: number;
  signerId: number;
  type: EsignFieldType;
  label: string | null;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  filled: boolean;
}

export interface EsignEvent {
  id: number;
  event: string;
  actor: string | null;
  ip: string | null;
  detail: string | null;
  createdAt: string;
}

export interface EsignListItem {
  id: number;
  title: string;
  status: EsignStatus;
  pageCount: number;
  createdByName: string | null;
  createdAt: string;
  sentAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  signers: EsignSigner[];
}

export interface EsignDetail extends EsignListItem {
  uid: string;
  originalFilename: string;
  originalSha256: string;
  signedSha256: string | null;
  fields: EsignField[];
  events: EsignEvent[];
}

export interface EsignCounts {
  all: number;
  draft: number;
  sent: number;
  completed: number;
  cancelled: number;
}

export interface EsignList {
  items: EsignListItem[];
  total: number;
  counts: EsignCounts;
}

export interface EsignPartnerOption {
  kind: 'client' | 'lead';
  id: number;
  name: string;
  ic: string | null;
  contact: string | null;
}

export interface EsignSignerInput {
  id?: number;
  firstName: string;
  lastName: string;
  email: string;
  partnerId: number | null;
  leadId: number | null;
}

export interface EsignFieldInput {
  signerId: number;
  type: EsignFieldType;
  label: string | null;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface EsignPartnerDocument {
  id: number;
  title: string;
  status: EsignStatus;
  createdAt: string;
  sentAt: string | null;
  completedAt: string | null;
  signers: { name: string; companyName: string | null; status: EsignSignerStatus; signedAt: string | null }[];
}

const BASE = '/api/v1/crm/esign';

export function listEsign(params: { status?: EsignStatus | ''; search?: string; limit?: number; offset?: number } = {}): Promise<EsignList> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.search) q.set('search', params.search);
  if (params.limit != null) q.set('limit', String(params.limit));
  if (params.offset != null) q.set('offset', String(params.offset));
  const qs = q.toString();
  return api.get<EsignList>(qs ? `${BASE}?${qs}` : BASE);
}

export function listEsignPartners(): Promise<EsignPartnerOption[]> {
  return api.get<EsignPartnerOption[]>(`${BASE}/partners`);
}

export function createEsign(input: { title: string; filename: string; contentBase64: string }): Promise<EsignDetail> {
  return api.post<EsignDetail>(BASE, input);
}

export function getEsign(id: number): Promise<EsignDetail> {
  return api.get<EsignDetail>(`${BASE}/${id}`);
}

export function renameEsign(id: number, title: string): Promise<EsignDetail> {
  return api.patch<EsignDetail>(`${BASE}/${id}`, { title });
}

export function saveEsignSigners(id: number, signers: EsignSignerInput[]): Promise<EsignDetail> {
  return api.put<EsignDetail>(`${BASE}/${id}/signers`, { signers });
}

export function saveEsignFields(id: number, fields: EsignFieldInput[]): Promise<EsignDetail> {
  return api.put<EsignDetail>(`${BASE}/${id}/fields`, { fields });
}

export function sendEsign(id: number): Promise<EsignDetail> {
  return api.post<EsignDetail>(`${BASE}/${id}/send`);
}

export function remindEsign(id: number): Promise<EsignDetail> {
  return api.post<EsignDetail>(`${BASE}/${id}/remind`);
}

export function cancelEsign(id: number): Promise<EsignDetail> {
  return api.post<EsignDetail>(`${BASE}/${id}/cancel`);
}

export function deleteEsignDraft(id: number): Promise<{ deleted: boolean }> {
  return api.del<{ deleted: boolean }>(`${BASE}/${id}`);
}

export function esignFileUrl(id: number, kind: EsignFileKind): string {
  return `${BASE}/${id}/file/${kind}`;
}

export function partnerEsignFileUrl(partnerId: number, documentId: number, kind: 'signed' | 'certificate'): string {
  return `/api/v1/affilmaster/klienti/${partnerId}/smlouvy/${documentId}/${kind}`;
}

export function listPartnerEsign(partnerId: number): Promise<EsignPartnerDocument[]> {
  return api.get<EsignPartnerDocument[]>(`/api/v1/affilmaster/klienti/${partnerId}/smlouvy`);
}

export async function fetchPdf(url: string): Promise<ArrayBuffer> {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    let message = 'Dokument se nepodařilo načíst';
    try {
      const body = (await res.json()) as { message?: string; error?: { message?: string } };
      message = body?.error?.message ?? body?.message ?? message;
    } catch {
      message = 'Dokument se nepodařilo načíst';
    }
    throw new ApiError(res.status, `HTTP_${res.status}`, message);
  }
  return res.arrayBuffer();
}

export type EsignPublicState = 'ready' | 'signed' | 'completed' | 'cancelled';

export interface EsignPublicField {
  id: number;
  type: EsignFieldType;
  label: string | null;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface EsignPublicView {
  state: EsignPublicState;
  document: { title: string; pageCount: number; senderName: string; senderCompany: string };
  signer: { firstName: string; lastName: string; email: string; companyName: string | null; consentGiven: boolean };
  fields: EsignPublicField[];
  signersTotal: number;
  signersSigned: number;
}

export type EsignSignValue =
  | { id: number; text: string }
  | { id: number; image: string; mode: EsignSignatureMode };

const PUBLIC_BASE = '/api/v1/crm/public/esign';

export function getEsignPublic(accessKey: string): Promise<EsignPublicView> {
  return api.get<EsignPublicView>(`${PUBLIC_BASE}/${encodeURIComponent(accessKey)}`);
}

export function esignPublicDocumentUrl(accessKey: string): string {
  return `${PUBLIC_BASE}/${encodeURIComponent(accessKey)}/document`;
}

export function esignPublicCertificateUrl(accessKey: string): string {
  return `${PUBLIC_BASE}/${encodeURIComponent(accessKey)}/certificate`;
}

export function consentEsignPublic(accessKey: string): Promise<{ consentGiven: boolean }> {
  return api.post<{ consentGiven: boolean }>(`${PUBLIC_BASE}/${encodeURIComponent(accessKey)}/consent`, { terms: true, identity: true });
}

export function signEsignPublic(accessKey: string, fields: EsignSignValue[]): Promise<{ state: EsignPublicState }> {
  return api.post<{ state: EsignPublicState }>(`${PUBLIC_BASE}/${encodeURIComponent(accessKey)}/sign`, { fields });
}
